import React, { useState, useEffect } from 'react';
import { Users, Search, Check, X } from 'lucide-react';
import api from '../../utils/api';

const EmployeeSelector = ({
    phase,
    selectedEmployees,
    onSelectionChange,
    assignmentType,
    onAssignmentTypeChange,
    selectedTeams = [],
    onTeamSelectionChange,
    assignmentMetadata = {},
    onMetadataChange
}) => {
    const [employees, setEmployees] = useState([]);
    const [teams, setTeams] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showTeamDropdown, setShowTeamDropdown] = useState(false);

    useEffect(() => {
        if (assignmentType === 'individual') {
            fetchEmployees();
        } else if (assignmentType === 'team') {
            fetchTeams();
        }
    }, [assignmentType]);

    const fetchTeams = async () => {
        setLoading(true);
        try {
            const response = await api.get('/teams');
            setTeams(response.data.teams || []);
        } catch (error) {
            console.error('Error fetching teams:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        setLoading(true);
        try {
            const response = await api.get('/tasks/company-employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEmployeeToggle = (employeeId) => {
        const newSelection = selectedEmployees.includes(employeeId)
            ? selectedEmployees.filter(id => id !== employeeId)
            : [...selectedEmployees, employeeId];

        onSelectionChange(newSelection);
    };

    const handleSelectAll = () => {
        const filteredIds = filteredEmployees.map(emp => emp.id);
        onSelectionChange(filteredIds);
    };

    const handleClearAll = () => {
        onSelectionChange([]);
    };

    const filteredEmployees = employees.filter(emp =>
        `${emp.first_name} ${emp.last_name} ${emp.email} ${emp.position || ''}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    const selectedEmployeeDetails = employees.filter(emp => selectedEmployees.includes(emp.id));

    return (
        <div className="space-y-4">
            {/* Assignment Type Selector */}
            <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Assignment Type
                </label>
                <div className="grid grid-cols-4 gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            onAssignmentTypeChange('role');
                            onSelectionChange([]);
                        }}
                        className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${assignmentType === 'role'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200'
                            }`}
                    >
                        By Role
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onAssignmentTypeChange('team');
                            onSelectionChange([]);
                            // Call optional callback if provided, though typically controlled by parent
                            if (onTeamSelectionChange) onTeamSelectionChange([]);
                        }}
                        className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${assignmentType === 'team'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200'
                            }`}
                    >
                        By Team
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onAssignmentTypeChange('individual');
                            onSelectionChange([]);
                        }}
                        className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${assignmentType === 'individual'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200'
                            }`}
                    >
                        Individual
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onAssignmentTypeChange('all');
                            onSelectionChange([]);
                        }}
                        className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${assignmentType === 'all'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200'
                            }`}
                    >
                        All Employees
                    </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
                    {assignmentType === 'role' && 'Task will be assigned to employees with specific roles'}
                    {assignmentType === 'team' && 'Task will be assigned to members of selected teams'}
                    {assignmentType === 'individual' && 'Select specific employees to assign this task'}
                    {assignmentType === 'all' && 'Task will be assigned to all employees in the company'}
                </p>
            </div>

            {/* Team Selector and Options */}
            {assignmentType === 'team' && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                            Select Teams
                        </label>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                            >
                                <span className="text-gray-700 dark:text-slate-200">
                                    {selectedTeams && selectedTeams.length === 0
                                        ? 'Select teams...'
                                        : `${selectedTeams ? selectedTeams.length : 0} team(s) selected`}
                                </span>
                                <Users className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                            </button>

                            {/* Team Dropdown */}
                            {showTeamDropdown && (
                                <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                    {loading ? (
                                        <div className="p-4 text-center text-gray-500 dark:text-slate-400">Loading teams...</div>
                                    ) : teams.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500 dark:text-slate-400">No teams found</div>
                                    ) : (
                                        teams.map(team => (
                                            <label key={team.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:bg-slate-800/50 cursor-pointer border-b border-gray-100 dark:border-slate-700/50 last:border-0">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTeams?.includes(team.id)}
                                                    onChange={() => {
                                                        const currentIds = selectedTeams || [];
                                                        const newIds = currentIds.includes(team.id)
                                                            ? currentIds.filter(id => id !== team.id)
                                                            : [...currentIds, team.id];
                                                        onTeamSelectionChange(newIds);
                                                    }}
                                                    className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                                                />
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-slate-100">{team.team_name}</p>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400">{team.member_count || 0} members</p>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                    <div className="p-2 sticky bottom-0 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-700/50">
                                        <button
                                            type="button"
                                            onClick={() => setShowTeamDropdown(false)}
                                            className="w-full py-2 bg-primary/10 text-primary text-sm font-medium rounded-lg hover:bg-primary/20"
                                        >
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Team Options */}
                    <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700 space-y-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Assignment Options</p>

                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={assignmentMetadata?.recursive || false}
                                onChange={(e) => onMetadataChange({ ...assignmentMetadata, recursive: e.target.checked })}
                                className="mt-1 w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                            />
                            <div>
                                <span className="text-sm text-gray-800 dark:text-slate-100 font-medium">Recursive Assignment</span>
                                <p className="text-xs text-gray-500 dark:text-slate-400">Assign to selected teams AND all their sub-teams.</p>
                            </div>
                        </label>

                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={assignmentMetadata?.leaders_only || false}
                                onChange={(e) => onMetadataChange({ ...assignmentMetadata, leaders_only: e.target.checked })}
                                className="mt-1 w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                            />
                            <div>
                                <span className="text-sm text-gray-800 dark:text-slate-100 font-medium">Team Leaders Only</span>
                                <p className="text-xs text-gray-500 dark:text-slate-400">Only assign to designated team leads, not all members.</p>
                            </div>
                        </label>
                    </div>
                </div>
            )}
            {assignmentType === 'individual' && (
                <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                        Select Employees
                    </label>

                    {/* Selected Employees Display */}
                    {selectedEmployeeDetails.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                            {selectedEmployeeDetails.map(emp => (
                                <span
                                    key={emp.id}
                                    className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                                >
                                    {emp.first_name} {emp.last_name}
                                    <button
                                        type="button"
                                        onClick={() => handleEmployeeToggle(emp.id)}
                                        className="hover:bg-primary/20 rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Dropdown Trigger */}
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowDropdown(!showDropdown)}
                            className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                        >
                            <span className="text-gray-700 dark:text-slate-200">
                                {selectedEmployees.length === 0
                                    ? 'Select employees...'
                                    : `${selectedEmployees.length} employee(s) selected`}
                            </span>
                            <Users className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                        </button>

                        {/* Dropdown */}
                        {showDropdown && (
                            <div className="absolute z-50 mt-2 w-full bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded-lg shadow-lg">
                                {/* Search */}
                                <div className="p-3 border-b border-gray-200 dark:border-slate-700">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                        <input
                                            type="text"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search employees..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="p-2 border-b border-gray-200 dark:border-slate-700 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSelectAll}
                                        className="flex-1 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded transition-colors"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleClearAll}
                                        className="flex-1 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:bg-slate-800/50 rounded transition-colors"
                                    >
                                        Clear All
                                    </button>
                                </div>

                                {/* Employee List */}
                                <div className="max-h-64 overflow-y-auto">
                                    {loading ? (
                                        <div className="p-4 text-center text-gray-500 dark:text-slate-400">
                                            Loading employees...
                                        </div>
                                    ) : filteredEmployees.length === 0 ? (
                                        <div className="p-4 text-center text-gray-500 dark:text-slate-400">
                                            No employees found
                                        </div>
                                    ) : (
                                        filteredEmployees.map(emp => (
                                            <label
                                                key={emp.id}
                                                className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:bg-slate-800/50 cursor-pointer transition-colors"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmployees.includes(emp.id)}
                                                    onChange={() => handleEmployeeToggle(emp.id)}
                                                    className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate">
                                                            {emp.first_name} {emp.last_name}
                                                        </p>
                                                        {selectedEmployees.includes(emp.id) && (
                                                            <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{emp.email}</p>
                                                    {emp.position && (
                                                        <p className="text-xs text-gray-400 dark:text-slate-500">{emp.position}</p>
                                                    )}
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="p-2 border-t border-gray-200 dark:border-slate-700 flex justify-between items-center">
                                    <span className="text-xs text-gray-500 dark:text-slate-400">
                                        {selectedEmployees.length} of {filteredEmployees.length} selected
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setShowDropdown(false)}
                                        className="px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded transition-colors"
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmployeeSelector;

